import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { HashRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { CenterToaster } from './components/CenterToaster';
import { authService } from './services/auth.service';
import { API_BASE_URL } from './services/api.client';
import { isUnavailabilityError, reportServerReachable, reportServerUnreachable } from './services/serverMonitor';
import { clientLicenseService, getCachedCheckResult, invalidateLicenseCheckCache } from './services/license.service';
import { PermissionProvider } from './hooks/usePermissions';
import { FeatureProvider } from './hooks/useFeatures';
import { ThemeProvider } from './contexts/ThemeContext';
import { AttendanceCodeProvider } from './contexts/AttendanceCodeContext';
import './App.css';
import ElectEmb from './components/ElectEmb';
import IdleTimeout from './components/IdleTimeout';

const Login = lazy(() => import('./components/Login'));
const PlatformLogin = lazy(() => import('./components/PlatformLogin'));
const PlatformDashboard = lazy(() => import('./components/PlatformDashboard'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const TrackDuty = lazy(() => import('./components/TrackDuty'));
const EssPayslip = lazy(() => import('./components/EssPayslip'));
const ActivateAccount = lazy(() => import('./components/ActivateAccount'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const LicenseActivation = lazy(() => import('./components/LicenseActivation'));


const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return authService.isAuthenticated() ? <>{children}</> : <Navigate to="/login" />;
};

const PlatformPrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return authService.isPlatformAuthenticated() ? <>{children}</> : <Navigate to="/platform/login" />;
};

// Check every 12 hours to detect revocation/suspension while the app is open.
const HEARTBEAT_INTERVAL_MS = 12 * 60 * 60 * 1000;

/**
 * Periodically polls GET /api/license/check to detect revocation or suspension
 * while the user is logged in. On failure, invalidates the cache and redirects
 * to the license screen. Network errors are ignored (benefit of the doubt).
 */
const LicenseHeartbeat: React.FC = () => {
  const navigate = useNavigate();
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const beat = async () => {
      if (window.location.hash.startsWith('#/platform')) return;
      const result = await clientLicenseService.check(true /* skipCache — always hit DB */);
      if (result !== null && !result.valid) {
        invalidateLicenseCheckCache();
        authService.logout?.();
        navigate('/license', { replace: true });
      }
    };

    timerRef.current = setInterval(beat, HEARTBEAT_INTERVAL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [navigate]);

  return null;
};

// Server reachability heartbeat. Live API traffic is monitored by the axios response
// interceptor, but an idle session (no request in flight) would not notice a stopped API.
// This polls GET /api/ping on a healthy cadence and feeds the result into the centralized
// serverMonitor, which owns the 5-second continuous-failure → auto-logout decision. Kept
// frequent enough that an idle session detects an outage promptly.
const HEARTBEAT_PING_MS = 3_000;    // healthy cadence
const HEARTBEAT_TIMEOUT_MS = 4_000; // per-ping timeout

/**
 * Pings the unauthenticated /api/ping endpoint while a user is logged in on a protected screen,
 * reporting reachability to the shared serverMonitor. Uses a bare axios call (no auth header)
 * so it never feeds itself through the interceptor. The monitor debounces a single blip and
 * only logs out after 5s of continuous failure.
 */
const ServerHeartbeat: React.FC = () => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let stopped = false;

    const schedule = (delay: number) => {
      if (stopped) return;
      timerRef.current = setTimeout(beat, delay);
    };

    const beat = async () => {
      // Only monitor a logged-in HRMS session on a protected screen.
      const hash = window.location.hash;
      if (!authService.isAuthenticated() ||
          hash.startsWith('#/login') || hash.startsWith('#/platform')) {
        reportServerReachable();
        schedule(HEARTBEAT_PING_MS);
        return;
      }

      try {
        await axios.get(`${API_BASE_URL}/ping`, { timeout: HEARTBEAT_TIMEOUT_MS });
        reportServerReachable();
      } catch (err) {
        if (isUnavailabilityError(err)) reportServerUnreachable();
        else reportServerReachable(); // a real HTTP response means the server is up
      }
      schedule(HEARTBEAT_PING_MS);
    };

    // Run the first check almost immediately so a server that is already down is caught fast.
    schedule(1_000);
    return () => { stopped = true; if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  return null;
};

/**
 * LicenseGate — server-authoritative license check before rendering any protected route.
 * Calls GET /api/license/check (with a 5-minute in-memory cache to avoid per-navigation
 * round trips). The server uses its own hardware fingerprint to query the DB — no
 * localStorage token, browser cache, or client-side state is involved.
 *
 *   valid → render children
 *   invalid → redirect to /license
 *   null (network error) → give benefit of the doubt, render children
 */
const LicenseGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Pre-populate from cache synchronously to avoid a loading flash on cached routes
  const cached = getCachedCheckResult();
  const [valid, setValid] = useState<boolean | null>(cached ? cached.valid : null);

  useEffect(() => {
    if (cached) return; // fresh cache hit — no network call needed
    clientLicenseService.check().then(result => {
      // null = server unreachable → give benefit of the doubt
      setValid(result === null || result.valid);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (valid === null) return <ElectEmb mode="overlay" message="Verifying license…" />;
  if (!valid) return <Navigate to="/license" replace />;
  return <>{children}</>;
};

function App() {
  return (
    <Router>
      <ThemeProvider>
      <AttendanceCodeProvider>
      <PermissionProvider>
      <FeatureProvider>
      <div className="App">
        {/* Center-screen popup notifications (Success / Warning / Error / Information) for every
            react-hot-toast call across the app — dimmed backdrop, auto-close, manual close, responsive. */}
        <CenterToaster />
        <IdleTimeout />
        <LicenseHeartbeat />
        <ServerHeartbeat />
        <Suspense fallback={<ElectEmb mode="overlay" message="Loading…" />}>
        <Routes>
          {/* ── License activation — shown on first launch before login ── */}
          <Route path="/license" element={<LicenseActivation />} />
          {/* Public no-login GPS sharing page — the SMS/WhatsApp tracking link.
              The GUID token in the URL is the sole authorisation (see FieldDutyController). */}
          <Route path="/track/:token" element={<TrackDuty />} />
            {/* Public payslip self-service behind the Payroll → PayQR Generator QR (signed token in ?t=). */}
            <Route path="/ess/payslip" element={<EssPayslip />} />

          {/* ── Platform admin — completely independent of HRMS license ── */}
          <Route path="/platform/login" element={<PlatformLogin />} />
          <Route path="/platform" element={<Navigate to="/platform/dashboard" replace />} />
          <Route path="/platform/*" element={<PlatformPrivateRoute><PlatformDashboard /></PlatformPrivateRoute>} />

          {/* ── Pre-login routes — public (no LicenseGate needed; dashboard routes are protected) ── */}
          <Route path="/login"            element={<Login />} />
          <Route path="/forgot-password"  element={<ForgotPassword />} />
          <Route path="/reset-password"   element={<ResetPassword />} />
          <Route path="/activate-account" element={<ActivateAccount />} />

          {/* ── Protected app routes ── */}
          <Route
            path="/:view/*"
            element={
              <LicenseGate>
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              </LicenseGate>
            }
          />
          <Route
            path="/dashboard"
            element={
              <LicenseGate>
                <PrivateRoute>
                  <Navigate to="/home" />
                </PrivateRoute>
              </LicenseGate>
            }
          />

          {/* ── Root: smart redirect — HRMS takes priority, then platform, then login ── */}
          <Route path="/" element={
            authService.isAuthenticated()
              ? <Navigate to="/home" replace />
              : authService.isPlatformAuthenticated()
                ? <Navigate to="/platform/dashboard" replace />
                : <Navigate to="/login" replace />
          } />
        </Routes>
        </Suspense>
      </div>
      </FeatureProvider>
      </PermissionProvider>
      </AttendanceCodeProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
